import React from "react";

import { enable, useIsEnabled } from "./_enabler";

const LazyDropdownMenu = React.lazy(async () => {
	// biome-ignore lint/style/noRestrictedImports: component usage
	const { DropdownMenu } = await import("@radix-ui/react-dropdown-menu");

	return { default: DropdownMenu };
});

export interface DropdownMenuProps extends React.ComponentProps<typeof LazyDropdownMenu> {
	/**
	 * When `true`, the dropdown menu component is loaded on hover/focus of the trigger,
	 * otherwise it is loaded when the component is mounted.
	 *
	 * @default true
	 */
	lazy?: boolean;
}

export const DropdownMenu: React.FC<DropdownMenuProps> = ({ lazy = true, children, ...props }) => {
	const isEnabled = useIsEnabled();

	if (!isEnabled) {
		return (
			<DisabledDropdownMenu open={!lazy || (props.open ?? props.defaultOpen ?? false)}>{children}</DisabledDropdownMenu>
		);
	}

	return (
		<React.Suspense fallback={children}>
			<LazyDropdownMenu modal={false} {...props}>
				{children}
			</LazyDropdownMenu>
		</React.Suspense>
	);
};

const DisabledDropdownMenu: React.FC<{
	open: boolean;
	children: React.ReactNode;
}> = ({ open, children }) => {
	React.useLayoutEffect(() => {
		// enable when programmatically opened
		if (open) {
			enable();
		}
	}, [open]);

	return <>{children}</>;
};
